import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { Button } from '@/components/ui/button';
import { BookOpen, X, Compass, Clock3, FolderOpen, ArrowRight } from 'lucide-react';

/* Resource Center Guides (P2) — each banner deep-links to its own module guide. */
const GUIDE_SEED_KEYS = {
  studio: 'guide.module_studio_v1',
  library: 'guide.module_evaluation_library_v1',
  resourceCenter: 'guide.module_resource_center_v1',
  roleProfiles: 'guide.module_role_profiles_v1',
  competencies: 'guide.module_competencies_v1',
};

/* Sprint Navigate — self-explaining module banner.
   Dismiss state persists per user-agent via localStorage. */
export function ModuleBanner({ id, icon: Icon, hideGuide = false }) {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const storageKey = `cth-module-banner-${id}`;
  const [dismissed, setDismissed] = useState(() => localStorage.getItem(storageKey) === '1');

  if (dismissed) return null;

  const rows = [
    { icon: Compass, label: t('moduleBanner.why', { defaultValue: 'Why it exists' }), text: t(`moduleBanners.${id}.purpose`) },
    { icon: Clock3, label: t('moduleBanner.when', { defaultValue: 'When to use it' }), text: t(`moduleBanners.${id}.when`) },
    { icon: FolderOpen, label: t('moduleBanner.what', { defaultValue: 'What it contains' }), text: t(`moduleBanners.${id}.contains`) },
    { icon: ArrowRight, label: t('moduleBanner.next', { defaultValue: 'Next step' }), text: t(`moduleBanners.${id}.next`) },
  ];

  return (
    <div
      className="relative rounded-lg border border-primary/20 bg-primary/5 p-4 sm:p-5"
      data-testid={`module-banner-${id}`}
    >
      <button
        type="button"
        onClick={() => { localStorage.setItem(storageKey, '1'); setDismissed(true); }}
        className="absolute right-3 top-3 rounded p-1 text-muted-foreground hover:bg-primary/10 hover:text-foreground transition-colors"
        aria-label={t('moduleBanner.dismiss', { defaultValue: 'Dismiss' })}
        data-testid={`module-banner-dismiss-${id}`}
      >
        <X className="h-4 w-4" />
      </button>

      <div className="flex items-start gap-3 pr-8">
        {Icon && (
          <div className="hidden sm:flex h-9 w-9 shrink-0 items-center justify-center rounded-md bg-primary/10 text-primary">
            <Icon className="h-5 w-5" />
          </div>
        )}
        <div className="min-w-0 flex-1">
          <h3 className="font-display text-sm font-bold text-foreground">
            {t(`moduleBanners.${id}.title`)}
          </h3>
          <div className="mt-2 grid grid-cols-1 md:grid-cols-2 gap-x-6 gap-y-1.5">
            {rows.map((r) => (
              <div key={r.label} className="flex items-start gap-2 text-xs leading-relaxed">
                <r.icon className="h-3.5 w-3.5 mt-0.5 shrink-0 text-primary/70" />
                <span className="text-muted-foreground">
                  <span className="font-semibold text-foreground/80">{r.label}: </span>
                  {r.text}
                </span>
              </div>
            ))}
          </div>
          {!hideGuide && (
            <Button
              size="sm"
              variant="outline"
              className="mt-3 gap-1.5 border-primary/30 text-primary hover:bg-primary/10"
              onClick={() => navigate(GUIDE_SEED_KEYS[id]
                ? `/admin/resource-center?open=${encodeURIComponent(GUIDE_SEED_KEYS[id])}`
                : '/admin/resource-center')}
              data-testid={`module-banner-guide-${id}`}
            >
              <BookOpen className="h-3.5 w-3.5" />
              {t('moduleBanner.viewGuide', { defaultValue: 'View Guide' })}
            </Button>
          )}
        </div>
      </div>
    </div>
  );
}
